import axios from 'axios';

const PROJECT_ID = import.meta.env.VITE_POSTHOG_PROJECT_ID;
const PERSONAL_KEY = import.meta.env.VITE_POSTHOG_PERSONAL_API_KEY;

const posthogApi = axios.create({
  baseURL: `https://us.posthog.com/api/projects/${PROJECT_ID}`,
  headers: {
    Authorization: `Bearer ${PERSONAL_KEY}`,
    'Content-Type': 'application/json',
  },
});


const POSTHOG_TIMEOUT = 20000;
const MAX_RETRIES = 3;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableError = (error: any) => {
  const status = error?.response?.status;
  return (
    !status ||
    status === 408 ||
    status === 429 ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
};

const postHogQuery = async <T = any>(query: unknown, retries = MAX_RETRIES): Promise<{ data: T }> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await posthogApi.post<T>('/query', query, {
        timeout: POSTHOG_TIMEOUT,
      });
      // We return { data: response.data } to keep compatibility with existing code assuming res.data
      return { data: response.data };
    } catch (error) {
      lastError = error;

      if (!isRetryableError(error) || attempt === retries) {
        throw error;
      }

      const delay = Math.min(1000 * 2 ** attempt, 5000);
      console.warn(`PostHog request failed (attempt ${attempt + 1}). Retrying in ${delay}ms...`, error);
      await sleep(delay);
    }
  }

  throw lastError;
};

export interface IDateFilter {
  from?: Date;
  to?: Date;
}

export type DateFilterParam = number | IDateFilter | undefined;

export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const buildDateCondition = (
  field: string = 'timestamp',
  dateParam?: DateFilterParam,
  defaultDays: number = 7
): string => {
  if (dateParam === undefined) {
    return defaultDays > 0 ? `AND ${field} >= now() - INTERVAL ${defaultDays} DAY` : '';
  }

  if (typeof dateParam === 'number') {
    return dateParam > 0 ? `AND ${field} >= now() - INTERVAL ${dateParam} DAY` : '';
  }

  const conditions: string[] = [];
  if (dateParam.from) {
    conditions.push(`toDate(${field}) >= '${formatLocalDate(dateParam.from)}'`);
  }
  if (dateParam.to) {
    conditions.push(`toDate(${field}) <= '${formatLocalDate(dateParam.to)}'`);
  }

  if (conditions.length === 0 && defaultDays > 0) {
    return `AND ${field} >= now() - INTERVAL ${defaultDays} DAY`;
  }

  return conditions.length > 0 ? `AND ${conditions.join(' AND ')}` : '';
};

export interface IAnalyticsData {
  listeningDurationHours: number; // Keeping for backward compatibility if needed
  totalListeningSeconds: number;
  premiumCount: number;
  freeCount: number;
  chartData: { name: string; value: number }[];
  premiumListeningSeconds?: number;
  freeListeningSeconds?: number;
}

export const fetchPostHogMetrics = async (dateFilter?: DateFilterParam): Promise<IAnalyticsData> => {
  // Query 1: Fetch active listening seconds grouped by user tier
  const durationQuery = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.user_tier,
          sum(JSONExtractFloat(properties, 'active_listening_seconds'))
        FROM events
        WHERE event = 'media_playback_event'
        ${buildDateCondition('timestamp', dateFilter, 7)}
        GROUP BY person.properties.user_tier
      `,
    },
  };

  // Query 2: Fetch current distribution of premium vs free users
  const userTiersQuery = {
    query: {
      kind: 'HogQLQuery',
      query:
        'SELECT properties.user_tier, count() FROM persons WHERE properties.user_tier IS NOT NULL GROUP BY properties.user_tier',
    },
  };

  try {
    const [durationRes, tiersRes] = await Promise.all([
      postHogQuery(durationQuery),
      postHogQuery(userTiersQuery),
    ]);

    const durationResults = durationRes.data.results || [];
    let totalSeconds = 0;
    let premiumListeningSeconds = 0;
    let freeListeningSeconds = 0;

    durationResults.forEach(([tier, sumSeconds]: [string | null, number]) => {
      const seconds = parseFloat(sumSeconds as any) || 0;
      totalSeconds += seconds;
      if (tier === 'premium') {
        premiumListeningSeconds += seconds;
      } else if (tier === 'free') {
        freeListeningSeconds += seconds;
      }
    });

    const listeningDurationHours = parseFloat((totalSeconds / 3600).toFixed(4));

    let premiumCount = 0;
    let freeCount = 0;

    const rawTiers = tiersRes.data.results || [];
    rawTiers.forEach(([tier, count]: [string, number]) => {
      if (tier === 'premium') premiumCount = count;
      if (tier === 'free') freeCount = count;
    });

    return {
      listeningDurationHours,
      totalListeningSeconds: totalSeconds,
      premiumCount,
      freeCount,
      chartData: [
        { name: 'Premium (QR Scanned)', value: premiumCount },
        { name: 'Free Tier (Direct)', value: freeCount },
      ],
      premiumListeningSeconds,
      freeListeningSeconds,
    };
  } catch (error) {
    console.error('Failed to fetch PostHog analytics:', error);
    throw error;
  }
};

export interface IUserPlaybackDuration {
  email: string;
  name: string;
  song: string;
  totalDurationSeconds: number;
  lastListened: string;
}

export const fetchUserPlaybackDurations = async (days: number = 7, limit: number = 50): Promise<IUserPlaybackDuration[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.email, 
          person.properties.name, 
          properties.content_title, 
          sum(JSONExtractFloat(properties, 'active_listening_seconds')), 
          max(timestamp) 
        FROM events 
        WHERE event = 'media_playback_event'
        AND timestamp >= now() - INTERVAL ${days} DAY 
        GROUP BY person.properties.email, person.properties.name, properties.content_title 
        ORDER BY sum(JSONExtractFloat(properties, 'active_listening_seconds')) DESC
        LIMIT ${limit}
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];

    return results.map(([email, name, song, duration, lastListened]: any) => ({
      email: email || 'N/A',
      name: name || 'Unknown User',
      song: song || 'Unknown Mantra/Song',
      totalDurationSeconds: parseFloat(duration) || 0,
      lastListened: lastListened || '',
    }));
  } catch (error) {
    console.error('Failed to fetch PostHog playback durations:', error);
    throw error;
  }
};

export interface IContentPlaybackDuration {
  song: string;
  totalPlays: number;
  totalDurationSeconds: number;
  lastPlayed: string;
}

export const fetchContentPlaybackDurations = async (
  dateFilter?: DateFilterParam,
  limit: number = 50
): Promise<IContentPlaybackDuration[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          properties.content_title, 
          countIf(properties.action = 'play'), 
          sum(JSONExtractFloat(properties, 'active_listening_seconds')), 
          max(timestamp) 
        FROM events 
        WHERE event = 'media_playback_event' AND properties.content_title IS NOT NULL
        ${buildDateCondition('timestamp', dateFilter, 7)}
        GROUP BY properties.content_title 
        ORDER BY countIf(properties.action = 'play') DESC
        LIMIT ${limit}
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];

    return results.map(([song, plays, duration, lastPlayed]: any) => ({
      song: song || 'Unknown Mantra/Song',
      totalPlays: parseInt(plays) || 0,
      totalDurationSeconds: parseFloat(duration) || 0,
      lastPlayed: lastPlayed || '',
    }));
  } catch (error) {
    console.error('Failed to fetch PostHog content playback durations:', error);
    throw error;
  }
};

export interface IUserActivityReport {
  email: string;
  name: string;
  userTier: 'free' | 'premium';
  songs: {
    title: string;
    durationSeconds: number;
    lastListened: string;
    plays: number;
  }[];
}

export const fetchUserPlaybackReport = async (
  email: string,
  name: string,
  days: number = 7,
  limit: number = 50
): Promise<IUserActivityReport> => {
  const playbackQuery = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.user_tier,
          properties.content_title,
          sum(JSONExtractFloat(properties, 'active_listening_seconds')),
          max(timestamp),
          countIf(properties.action = 'play')
        FROM events
        WHERE event = 'media_playback_event' AND person.properties.email = '${email}'
        AND timestamp >= now() - INTERVAL ${days} DAY
        GROUP BY person.properties.user_tier, properties.content_title
        ORDER BY sum(JSONExtractFloat(properties, 'active_listening_seconds')) DESC
        LIMIT ${limit}
      `,
    },
  };

  const tierQuery = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT properties.user_tier 
        FROM persons 
        WHERE properties.email = '${email}'
        LIMIT 1
      `,
    },
  };

  try {
    const [playbackRes, tierRes] = await Promise.all([
      postHogQuery(playbackQuery),
      postHogQuery(tierQuery),
    ]);

    const playbackResults = playbackRes.data.results || [];
    const tierResults = tierRes.data.results || [];

    let userTier: 'free' | 'premium' = 'free';
    if (tierResults.length > 0 && tierResults[0][0]) {
      userTier = tierResults[0][0] as 'free' | 'premium';
    } else if (playbackResults.length > 0 && playbackResults[0][0]) {
      userTier = playbackResults[0][0] as 'free' | 'premium';
    }

    const songs = playbackResults.map(([, song, duration, lastListened, plays]: any) => ({
      title: song || 'Unknown Mantra/Song',
      durationSeconds: parseFloat(duration) || 0,
      lastListened: lastListened || '',
      plays: parseInt(plays) || 0,
    }));

    return {
      email,
      name,
      userTier,
      songs,
    };
  } catch (error) {
    console.error(`Failed to fetch PostHog playback report for ${email}:`, error);
    return {
      email,
      name,
      userTier: 'free',
      songs: [],
    };
  }
};

export const fetchAllUserTiers = async (days: number = 7): Promise<Record<string, 'free' | 'premium'>> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT person.properties.email, person.properties.user_tier
        FROM events
        WHERE person.properties.email IS NOT NULL AND person.properties.user_tier IS NOT NULL
        AND timestamp >= now() - INTERVAL ${days} DAY
        GROUP BY person.properties.email, person.properties.user_tier
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    const mapping: Record<string, 'free' | 'premium'> = {};
    results.forEach(([email, tier]: [string, 'free' | 'premium']) => {
      if (email) mapping[email.toLowerCase().trim()] = tier;
    });
    return mapping;
  } catch (error) {
    console.error('Failed to fetch user tiers mapping from PostHog:', error);
    return {};
  }
};

export interface IPostHogPremiumUser {
  name: string;
  email: string;
  mobile: string;
}

export const fetchPostHogPremiumUsers = async (
  dateFilter?: DateFilterParam,
  limit: number = 50
): Promise<IPostHogPremiumUser[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT properties.name, properties.email, properties.mobile
        FROM persons
        WHERE properties.user_tier = 'premium' AND properties.email IS NOT NULL
        ${buildDateCondition('created_at', dateFilter, 7)}
        ORDER BY created_at DESC
        LIMIT ${limit}
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([name, email, mobile]: [string, string, string]) => ({
      name: name || 'Unknown User',
      email: email || 'N/A',
      mobile: mobile || 'N/A',
    }));
  } catch (error) {
    console.error('Failed to fetch premium users from PostHog:', error);
    throw error;
  }
};

export interface IPostHogUserReport {
  name: string;
  email: string;
  userTier: string;
  createdAt: string;
}

export const fetchPostHogAllUsers = async (): Promise<IPostHogUserReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT properties.name, properties.email, properties.user_tier, created_at
        FROM persons
        ORDER BY created_at DESC
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([name, email, tier, createdAt]: [string, string, string, string]) => ({
      name: name || 'Unknown User',
      email: email || 'N/A',
      userTier: tier || 'free',
      createdAt: createdAt || '',
    }));
  } catch (error) {
    console.error('Failed to fetch all users from PostHog:', error);
    throw error;
  }
};

export interface IGuestUserReport {
  ip: string;
  city: string;
  state: string;
  country: string;
  timestamp: string;
}

export const fetchPostHogGuestUsers = async (
  dateFilter?: DateFilterParam,
  limit: number = 100
): Promise<IGuestUserReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          properties.$ip, 
          properties.$geoip_city_name, 
          properties.$geoip_subdivision_1_name, 
          properties.$geoip_country_name, 
          timestamp 
        FROM events 
        WHERE event = 'login_page_visit' 
        ${buildDateCondition('timestamp', dateFilter, 7)}
        ORDER BY timestamp DESC
        LIMIT ${limit}
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([ip, city, state, country, timestamp]: any) => ({
      ip: ip || 'Anonymous / Masked',
      city: city || 'Unknown City',
      state: state || 'Unknown State',
      country: country || 'Unknown Country',
      timestamp: timestamp || '',
    }));
  } catch (error) {
    console.error('Failed to fetch guest users from PostHog:', error);
    throw error;
  }
};

export interface IUserEventReport {
  event: string;
  ip: string;
  path: string;
  timestamp: string;
}

export const fetchPostHogUserEvents = async (email: string, days: number = 7, limit: number = 50): Promise<IUserEventReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          event,
          properties.$ip,
          properties.$current_url,
          timestamp,
          properties.event_type,
          properties.element_name
        FROM events
        WHERE person.properties.email = '${email}'
        ORDER BY timestamp DESC
        LIMIT ${limit}
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([event, ip, url, timestamp, eventType, elementName]: any) => {
      let eventName = event;
      if (event === 'user_event') {
        if (eventType === 'button_click') {
          eventName = `Button Clicked: "${elementName || 'button'}"`;
        } else if (eventType === 'input_field_interaction') {
          eventName = `Input Field Interacted: "${elementName || 'field'}"`;
        }
      } else if (event === '$pageview') {
        eventName = 'Page Opened';
      } else if (event === 'user_login') {
        eventName = 'User Logged In';
      } else if (event === 'user_signup') {
        eventName = 'User Signed Up';
      } else if (event === 'media_playback_event') {
        eventName = 'Audio Playback';
      } else if (event === 'content_unlocked') {
        eventName = 'Content Unlocked';
      } else if (event === 'login_page_visit') {
        eventName = 'Login Page Visited (Guest)';
      }

      let path = '/';
      try {
        if (url) {
          const parsed = new URL(url);
          path = parsed.pathname + parsed.search;
        }
      } catch (e) {
        path = url || '/';
      }

      return {
        event: eventName,
        ip: ip || 'Anonymous',
        path: path,
        timestamp: timestamp || '',
      };
    });
  } catch (error) {
    console.error(`Failed to fetch user events for ${email}:`, error);
    throw error;
  }
};

export interface IAllUsersEventReport {
  name: string;
  email: string;
  event: string;
  timestamp: string;
}

export const fetchAllUsersEventsReport = async (): Promise<IAllUsersEventReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.name,
          person.properties.email,
          event,
          properties.event_type,
          properties.element_name,
          timestamp
        FROM events
        WHERE person.properties.email IS NOT NULL
        ORDER BY timestamp DESC
        LIMIT 5000
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([name, email, event, eventType, elementName, timestamp]: any) => {
      let eventName = event;
      if (event === 'user_event') {
        if (eventType === 'button_click') {
          eventName = `Button Clicked: "${elementName || 'button'}"`;
        } else if (eventType === 'input_field_interaction') {
          eventName = `Input Field Interacted: "${elementName || 'field'}"`;
        }
      } else if (event === '$pageview') {
        eventName = 'Page Opened';
      } else if (event === 'user_login') {
        eventName = 'User Logged In';
      } else if (event === 'user_signup') {
        eventName = 'User Signed Up';
      } else if (event === 'media_playback_event') {
        eventName = 'Audio Playback';
      } else if (event === 'content_unlocked') {
        eventName = 'Content Unlocked';
      }

      return {
        name: name || 'Unknown User',
        email: email || 'N/A',
        event: eventName,
        timestamp: timestamp ? new Date(timestamp).toLocaleString() : 'N/A',
      };
    });
  } catch (error) {
    console.error('Failed to fetch all users events report:', error);
    throw error;
  }
};

export interface IGiftOneReport {
  name: string;
  email: string;
  mantraName: string;
  link: string;
  timestamp: string;
}

export const fetchPostHogUserGiftClicks = async (email: string, limit: number = 50): Promise<IGiftOneReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.name,
          person.properties.email,
          properties.mantra_name,
          properties.link,
          timestamp
        FROM events
        WHERE event = 'gift_one_click' AND person.properties.email = '${email}'
        ORDER BY timestamp DESC
        LIMIT ${limit}

      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([name, userEmail, mantraName, link, timestamp]: any) => ({
      name: name || 'Unknown User',
      email: userEmail || email,
      mantraName: mantraName || 'Unknown Mantra',
      link: link || '',
      timestamp: timestamp || '',
    }));
  } catch (error) {
    console.error(`Failed to fetch gift clicks for ${email}:`, error);
    throw error;
  }
};

export const fetchPostHogAllGiftClicks = async (): Promise<IGiftOneReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.name,
          person.properties.email,
          properties.mantra_name,
          properties.link,
          timestamp
        FROM events
        WHERE event = 'gift_one_click'
        ORDER BY timestamp DESC
        LIMIT 5000
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([name, email, mantraName, link, timestamp]: any) => ({
      name: name || 'Unknown User',
      email: email || 'N/A',
      mantraName: mantraName || 'Unknown Mantra',
      link: link || '',
      timestamp: timestamp || '',
    }));
  } catch (error) {
    console.error('Failed to fetch all gift clicks from PostHog:', error);
    throw error;
  }
};

export interface IGetOneReport {
  name: string;
  email: string;
  mantraName: string;
  link: string;
  timestamp: string;
}

export const fetchPostHogUserGetClicks = async (email: string, limit: number = 50): Promise<IGetOneReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.name,
          person.properties.email,
          properties.mantra_name,
          properties.link,
          timestamp
        FROM events
        WHERE event = 'get_one_click' AND person.properties.email = '${email}'
        ORDER BY timestamp DESC
        LIMIT ${limit}
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([name, userEmail, mantraName, link, timestamp]: any) => ({
      name: name || 'Unknown User',
      email: userEmail || email,
      mantraName: mantraName || 'Unknown Mantra',
      link: link || '',
      timestamp: timestamp || '',
    }));
  } catch (error) {
    console.error(`Failed to fetch get clicks for ${email}:`, error);
    throw error;
  }
};

export const fetchPostHogAllGetClicks = async (): Promise<IGetOneReport[]> => {
  const query = {
    query: {
      kind: 'HogQLQuery',
      query: `
        SELECT 
          person.properties.name,
          person.properties.email,
          properties.mantra_name,
          properties.link,
          timestamp
        FROM events
        WHERE event = 'get_one_click'
        ORDER BY timestamp DESC
        LIMIT 5000
      `,
    },
  };

  try {
    const res = await postHogQuery(query);
    const results = res.data.results || [];
    return results.map(([name, email, mantraName, link, timestamp]: any) => ({
      name: name || 'Unknown User',
      email: email || 'N/A',
      mantraName: mantraName || 'Unknown Mantra',
      link: link || '',
      timestamp: timestamp || '',
    }));
  } catch (error) {
    console.error('Failed to fetch all get clicks from PostHog:', error);
    throw error;
  }
};

